import type { Cell, GamePhase, ScorePopup, TerritoryMap } from '../game/types.ts'
import {
  TERRITORY_HIGHLIGHT_MIN,
  TERRITORY_COLORS,
  TERRITORY_OPACITY,
} from '../config/scoring.ts'
import { resolveTileImage, type TileMap } from '../config/tiles.ts'
import block1 from '../assets/block1.webp'
import block2 from '../assets/block2.webp'
import { t, tx } from '../i18n/translations.ts'

interface Props {
  board: Cell[][]
  territory: {
    black: TerritoryMap
    white: TerritoryMap
  }
  winLine: [number, number][] | null
  flowers: boolean[][]
  map: TileMap
  turnCount: number
  turn: 'black' | 'white'
  playerColor: 'black' | 'white'
  phase: GamePhase
  // finale 演出でバウンドさせるセル群（5連の勝利ライン or 勝者の駒全て）
  finaleHighlight: [number, number][]
  // 5連勝利時はキラキラを追加表示
  finaleSparkle: boolean
  scorePopups: ScorePopup[]
  onCellClick: (row: number, col: number) => void
  onPopupComplete: (id: number) => void
}

// 駒アイコンは「色」ベースで固定（先手 black = 青系 / 後手 white = 赤系）。
// alt テキスト（あなた/CPU）は playerColor で動的決定。
const ICON_BY_COLOR: Record<'black' | 'white', string> = {
  black: block1,
  white: block2,
}

function territoryColor(owner: 'black' | 'white', len: number): string {
  const { r, g, b } = TERRITORY_COLORS[owner]
  const alpha = TERRITORY_OPACITY[len] ?? TERRITORY_OPACITY[TERRITORY_HIGHLIGHT_MIN]
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function getTerritoryStyle(blackLen: number, whiteLen: number): React.CSSProperties {
  if (blackLen >= 5 || whiteLen >= 5) return {}

  if (blackLen >= TERRITORY_HIGHLIGHT_MIN && blackLen >= whiteLen) {
    return { backgroundColor: territoryColor('black', blackLen) }
  }

  if (whiteLen >= TERRITORY_HIGHLIGHT_MIN && whiteLen > blackLen) {
    return { backgroundColor: territoryColor('white', whiteLen) }
  }

  return {}
}

export function Board({
  board,
  territory,
  winLine,
  flowers,
  map,
  turnCount,
  turn,
  playerColor,
  phase,
  finaleHighlight,
  finaleSparkle,
  scorePopups,
  onCellClick,
  onPopupComplete,
}: Props) {
  const winLineSet = new Set<string>()
  if (winLine) {
    for (const [wr, wc] of winLine) {
      winLineSet.add(`${wr}-${wc}`)
    }
  }

  // finale 演出対象セルのルックアップ。bounceIndex は アニメ遅延のスタガに使う。
  const bounceIndex = new Map<string, number>()
  finaleHighlight.forEach(([r, c], i) => bounceIndex.set(`${r}-${c}`, i))

  // セル単位でポップアップをグループ化（同マスに複数つく可能性は低いが防御的に）
  const popupsByCell = new Map<string, ScorePopup[]>()
  for (const p of scorePopups) {
    const key = `${p.row}-${p.col}`
    const list = popupsByCell.get(key)
    if (list) list.push(p)
    else popupsByCell.set(key, [p])
  }

  return (
    <div className="board">
      {board.map((row, r) =>
        row.map((cell, c) => {
          const key = `${r}-${c}`
          const blackLen = territory.black[r][c]
          const whiteLen = territory.white[r][c]
          const hasFlower = flowers[r][c]
          const isWinCell = winLineSet.has(key)
          const isClickable =
            phase === 'game' && turn === playerColor && cell === 'empty'

          const bounceIdx = bounceIndex.get(key)
          const isBouncing = bounceIdx !== undefined && phase === 'finale'
          const isSparkling = isBouncing && finaleSparkle

          let className = 'cell'
          if (isClickable) className += ' cell--clickable'
          if (isWinCell) className += ' cell--win'
          if (cell === 'black') className += ' cell--black'
          if (cell === 'white') className += ' cell--white'
          if (isBouncing) className += ' cell--bounce'
          if (isSparkling) className += ' cell--sparkle'

          const territoryStyle = getTerritoryStyle(blackLen, whiteLen)
          const cellStyle: React.CSSProperties = {
            ...territoryStyle,
            backgroundImage: `url(${resolveTileImage(map[r][c], turnCount)})`,
          }
          // バウンド開始のスタガ（最大 480ms 内に分散させる）
          const pieceStyle: React.CSSProperties | undefined = isBouncing
            ? { animationDelay: `${(bounceIdx % 6) * 80}ms` }
            : undefined

          const cellPopups = popupsByCell.get(key)
          const occupantLabel =
            cell === 'empty' ? null : cell === playerColor ? t('boardPlayerPiece') : t('boardCpuPiece')
          const ariaLabel = [
            tx('boardCellPosition', { row: r + 1, col: c + 1 }),
            occupantLabel ?? (isClickable ? t('boardPlaceable') : t('boardEmpty')),
            hasFlower && cell === 'empty' ? t('boardFlower') : null,
          ].filter(Boolean).join('、')

          return (
            <button
              type="button"
              key={key}
              className={className}
              style={cellStyle}
              onClick={isClickable ? () => onCellClick(r, c) : undefined}
              disabled={!isClickable}
              aria-label={ariaLabel}
            >
              {cell !== 'empty' && (
                <img
                  src={ICON_BY_COLOR[cell]}
                  alt={cell === playerColor ? t('boardPlayerAlt') : t('boardCpuAlt')}
                  className="piece-img"
                  style={pieceStyle}
                />
              )}
              {hasFlower && cell === 'empty' && (
                <span className="flower">🌸</span>
              )}
              {/* 4連警告マーク */}
              {(blackLen === 4 || whiteLen === 4) && cell !== 'empty' && (
                <span className="territory-badge">4!</span>
              )}
              {/* スコア獲得ポップアップ（アニメーション完了で消去） */}
              {cellPopups?.map(p => (
                <span
                  key={p.id}
                  className={`score-popup score-popup--${p.color}`}
                  onAnimationEnd={() => onPopupComplete(p.id)}
                >
                  +{p.gain}
                </span>
              ))}
            </button>
          )
        })
      )}
    </div>
  )
}
